import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'

import { Button } from '../../ui/Button/Button'
import { Card } from '../../ui/Card/Card'
import { Spinner } from '../../ui/Spinner/Spinner'
import { useAuth } from '../auth/AuthProvider'
import type { DashboardUser } from '../auth/auth-adapter'
import {
  getOnboardingApiClient,
  OnboardingApiError,
  type OnboardingApiClient,
} from './onboarding-api'
import { onboardingStatusQueryOptions } from './onboarding-query'
import { destinationPath, routeOnboardingResponse } from './onboarding-routing'
import styles from './OnboardingLayout.module.css'

export function VerifyEmailPage({
  client = getOnboardingApiClient(),
}: {
  client?: OnboardingApiClient
}) {
  const auth = useAuth()
  if (auth.status !== 'authenticated') {
    return <Spinner label="Restoring your account" />
  }
  return <AuthenticatedVerifyEmailPage client={client} user={auth.user} />
}

function AuthenticatedVerifyEmailPage({
  client,
  user,
}: {
  client: OnboardingApiClient
  user: DashboardUser
}) {
  const auth = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [working, setWorking] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const query = useQuery(onboardingStatusQueryOptions(client, user))

  if (query.isError) {
    if (
      query.error instanceof OnboardingApiError &&
      query.error.code === 'unauthorized'
    ) {
      return (
        <Navigate
          to="/login"
          replace
          state={{ from: location.pathname, reauthenticate: true }}
        />
      )
    }
    const classificationRequired =
      query.error instanceof OnboardingApiError &&
      query.error.code === 'classification-required'
    return (
      <Card className={styles.panel}>
        <h1>
          {classificationRequired
            ? 'Your account needs review'
            : 'Verification status could not be loaded'}
        </h1>
        <p>
          {classificationRequired
            ? 'Flio support must classify this account before setup can continue.'
            : 'Please retry. If the problem continues, contact Flio support.'}
        </p>
        <div className={styles.actions}>
          <Button type="button" onClick={() => void query.refetch()}>
            Retry
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => void auth.logout()}
          >
            Sign out
          </Button>
        </div>
      </Card>
    )
  }

  if (query.data) {
    const destination = routeOnboardingResponse(query.data)
    if (destination !== 'verify-email') {
      return <Navigate to={destinationPath(destination)} replace />
    }
  }

  async function resend() {
    setWorking(true)
    setMessage(null)
    try {
      await auth.sendVerification()
      setMessage('Verification email sent. Check your inbox and spam folder.')
    } catch {
      setMessage('Verification email could not be sent. Please try again.')
    } finally {
      setWorking(false)
    }
  }

  async function refresh() {
    setWorking(true)
    setMessage(null)
    try {
      const user = await auth.reloadCurrentUser()
      if (!user.emailVerified) {
        setMessage('Your email is not verified yet.')
        return
      }
      const result = await query.refetch()
      if (!result.data) throw new Error('Onboarding status was unavailable')
      void navigate(destinationPath(routeOnboardingResponse(result.data)), {
        replace: true,
      })
    } catch {
      setMessage('Verification could not be confirmed. Please retry.')
    } finally {
      setWorking(false)
    }
  }

  return (
    <Card className={styles.panel}>
      <h1>Verify your email</h1>
      <p>
        We sent a verification link to <strong>{user.email}</strong>. Verify it
        before continuing clinic setup.
      </p>
      {message ? <p role="status">{message}</p> : null}
      <div className={styles.actions}>
        <Button type="button" disabled={working} onClick={() => void refresh()}>
          I verified my email
        </Button>
        <Button
          type="button"
          variant="secondary"
          disabled={working}
          onClick={() => void resend()}
        >
          Resend verification email
        </Button>
        <Button
          type="button"
          variant="ghost"
          disabled={working}
          onClick={() => void auth.logout()}
        >
          Sign out
        </Button>
      </div>
    </Card>
  )
}
